import React, { useState, useEffect } from 'react';
import { Settings, Key, RefreshCw, CheckCircle, XCircle, ArrowLeft, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import api from '../utils/api';

export default function AdminSettings() {
  const [settings, setSettings] = useState({
    client_id: '',
    client_secret: '',
    refresh_token: ''
  });
  const [loading, setLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [currentConfig, setCurrentConfig] = useState(null);
  const [lastSync, setLastSync] = useState(null);

  useEffect(() => {
    fetchCurrentSettings();
    fetchLastSync();
  }, []);

  const fetchCurrentSettings = async () => {
    try {
      const response = await api.get('/zoho/settings');
      setCurrentConfig(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const fetchLastSync = async () => {
    try {
      const response = await api.get('/zoho/last-sync');
      setLastSync(response.data.last_sync);
    } catch (error) {
      console.error('Failed to fetch last sync:', error);
    }
  };

  const handleSave = async () => {
    if (!settings.client_id || !settings.client_secret || !settings.refresh_token) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await api.post('/zoho/settings', settings);
      toast.success('Zoho settings saved successfully');
      fetchCurrentSettings();
      setTestResult(null);
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    setLoading(true);
    setTestResult(null);
    try {
      const response = await api.get('/zoho/test-connection');
      setTestResult(response.data);
      if (response.data.success) {
        toast.success('Connection successful!');
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      setTestResult({ success: false, message: error.response?.data?.detail || 'Connection failed' });
      toast.error('Connection test failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setLoading(true);
    try {
      await api.post('/zoho/sync');
      toast.success('Sync started! Jobs will be updated shortly.');
      setTimeout(fetchLastSync, 5000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-50 to-cyan-50/30">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <Link to="/dashboard/admin" className="inline-flex items-center text-brand-blue hover:underline mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Link>

        <div className="glass-morphism rounded-3xl p-8 shadow-xl">
          <div className="flex items-center gap-3 mb-8">
            <div className="p-3 bg-gradient-to-r from-brand-gradient-start to-brand-gradient-end rounded-xl">
              <Settings className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-zinc-900">Zoho Recruit Integration</h1>
              <p className="text-zinc-500">Configure your Zoho Recruit API connection</p>
            </div>
          </div>

          {/* Current Status */}
          <div className="bg-zinc-50 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-zinc-800 mb-4">Current Status</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                {currentConfig?.configured ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-500" />
                )}
                <span className="text-zinc-700">
                  {currentConfig?.configured ? 'Credentials configured' : 'Not configured'}
                </span>
              </div>
              <div className="text-zinc-600">
                Last sync: {lastSync ? new Date(lastSync).toLocaleString() : 'Never'}
              </div>
            </div>
            {currentConfig?.client_id_preview && (
              <p className="text-sm text-zinc-500 mt-2">
                Client ID: {currentConfig.client_id_preview}
              </p>
            )}
          </div>

          {/* Setup Instructions */}
          <div className="bg-blue-50 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-blue-800 mb-3">Setup Instructions</h3>
            <ol className="list-decimal list-inside text-blue-700 space-y-2 text-sm">
              <li>Go to <a href="https://api-console.zoho.com/" target="_blank" rel="noopener noreferrer" className="underline">Zoho API Console <ExternalLink className="w-3 h-3 inline" /></a></li>
              <li>Create a Self Client (Server-based Application)</li>
              <li>Add scope: <code className="bg-blue-100 px-2 py-0.5 rounded">ZohoRecruit.modules.ALL</code></li>
              <li>Generate a refresh token using the Self Client</li>
              <li>Copy your Client ID, Client Secret, and Refresh Token below</li>
            </ol>
          </div>

          {/* Credentials Form */}
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                <Key className="w-4 h-4 inline mr-2" />
                Client ID *
              </label>
              <Input
                type="text"
                placeholder="Enter your Zoho Client ID"
                value={settings.client_id}
                onChange={(e) => setSettings({ ...settings, client_id: e.target.value })}
                className="h-12 rounded-xl"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                <Key className="w-4 h-4 inline mr-2" />
                Client Secret *
              </label>
              <Input
                type="password"
                placeholder="Enter your Zoho Client Secret"
                value={settings.client_secret}
                onChange={(e) => setSettings({ ...settings, client_secret: e.target.value })}
                className="h-12 rounded-xl"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                <Key className="w-4 h-4 inline mr-2" />
                Refresh Token *
              </label>
              <Input
                type="password"
                placeholder="Enter your Zoho Refresh Token"
                value={settings.refresh_token}
                onChange={(e) => setSettings({ ...settings, refresh_token: e.target.value })}
                className="h-12 rounded-xl"
              />
            </div>

            {/* Test Result */}
            {testResult && (
              <div className={`p-4 rounded-xl ${testResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                <div className="flex items-center gap-2">
                  {testResult.success ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    <XCircle className="w-5 h-5" />
                  )}
                  <span>{testResult.message}</span>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-wrap gap-4 pt-4">
              <Button
                onClick={handleSave}
                disabled={loading}
                className="bg-gradient-to-r from-brand-gradient-start to-brand-gradient-end text-white font-semibold rounded-xl px-8"
              >
                {loading ? 'Saving...' : 'Save Credentials'}
              </Button>

              <Button
                onClick={handleTestConnection}
                disabled={loading || !currentConfig?.configured}
                variant="outline"
                className="rounded-xl px-8"
              >
                Test Connection
              </Button>

              <Button
                onClick={handleSync}
                disabled={loading || !currentConfig?.configured}
                variant="outline"
                className="rounded-xl px-8"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Sync Now
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
